import { ActivityData, ClassroomStatus } from '../../../../interfaces/activity';
import {
    ActivityLMSType,
    ActivityModality,
    ActivityProgram,
    ActivityStatus,
    ActivityType,
} from '../../Common/Common';

// Configuration constants
export const CLASSROOM_CONFIG = {
    POLLING_INTERVAL: 2000,
    MIN_CLASS_SIZE: 1,
    SUPPORTED_ACTIVITY_TYPES: [ActivityType.Class, ActivityType.TTT],
    SUPPORTED_PROGRAMS: [
        ActivityProgram.Commercial,
        ActivityProgram.Partner,
        ActivityProgram.SkillsCenters,
        ActivityProgram.WorldWideFieldEnablement,
        ActivityProgram.PartnerReseller,
        ActivityProgram.AAI,
    ],
    LMS_REQUIREMENTS: {
        [ActivityLMSType.Kiku]: ['vIltId'],
        [ActivityLMSType.CustomerLMS]: ['vIltId', 'vIltMeetingId'],
        [ActivityLMSType.MyClass]: ['vIltId'],
        [ActivityLMSType.SkillBuilder]: ['vIltId'],
    },
    TERMINAL_STATUSES: [
        ClassroomStatus.CLASSROOM_UPDATE_FAILED,
        ClassroomStatus.CANCELLED,
        ClassroomStatus.CLASSROOM_PROVISIONING_FAILED,
        ClassroomStatus.CLASSROOM_READY,
        ClassroomStatus.READY_FOR_CLASSROOM_CREATION,
    ],
} as const;

// Basic validation functions
export const isActivityTypeSupported = (type: string): boolean =>
    CLASSROOM_CONFIG.SUPPORTED_ACTIVITY_TYPES.some(
        (supportedType) => supportedType === type,
    );

export const isProgramSupported = (program: string): boolean =>
    CLASSROOM_CONFIG.SUPPORTED_PROGRAMS.some(
        (supportedProgram) => supportedProgram === program,
    );

export const hasRequiredInstructors = (instructors?: any[]): boolean =>
    Boolean(instructors && instructors.length > 0);

export const hasValidClassSize = (size?: number): boolean =>
    Boolean(size && size >= CLASSROOM_CONFIG.MIN_CLASS_SIZE);

export const isActivityActive = (status: string): boolean =>
    status === ActivityStatus.Active;

// LMS-specific validation
export const validateLMSRequirements = (activity: ActivityData): boolean => {
    const { activity_modality, lms_type, v_ilt_id, v_ilt_meeting_id } =
        activity;

    // Bypass LMS requirements for ILT modality
    if (activity_modality === ActivityModality.ILT) {
        return true;
    }

    // Check LMS-specific requirements
    const requirements =
        CLASSROOM_CONFIG.LMS_REQUIREMENTS[lms_type as ActivityLMSType];
    if (!requirements) {
        return false;
    }

    const hasVIltId = Boolean(v_ilt_id);
    const hasVIltMeetingId = Boolean(v_ilt_meeting_id);

    return requirements.every((req) => {
        if (req === 'vIltId') {
            return hasVIltId;
        }
        if (req === 'vIltMeetingId') {
            return hasVIltMeetingId;
        }
        return false;
    });
};

// Comprehensive validation with reasons
export interface EligibilityCheck {
    isEligible: boolean;
    reasons: string[];
    checks: {
        isActive: boolean;
        supportedProgram: boolean;
        hasInstructors: boolean;
        hasValidSize: boolean;
        lmsRequirementsMet: boolean;
    };
}

export const validateClassroomEligibility = (
    activity: ActivityData,
): EligibilityCheck => {
    const checks = {
        isActive: isActivityActive(activity.activity_status),
        supportedProgram: isProgramSupported(activity.program),
        hasInstructors: hasRequiredInstructors(activity.instructors),
        hasValidSize: hasValidClassSize(activity.class_size),
        lmsRequirementsMet: validateLMSRequirements(activity),
    };

    const reasons: string[] = [];
    if (!checks.isActive) reasons.push('Activity must be Active');
    if (!checks.supportedProgram)
        reasons.push('Program type not supported for classrooms');
    if (!checks.hasInstructors)
        reasons.push('At least one instructor must be assigned');
    if (!checks.hasValidSize) reasons.push('Class size must be at least 1');
    if (!checks.lmsRequirementsMet)
        reasons.push('LMS requirements not met for virtual delivery');

    return {
        isEligible: Object.values(checks).every(Boolean),
        reasons,
        checks,
    };
};

// Status-related functions
export const hasExistingClassroom = (status?: string): boolean =>
    Boolean(
        status &&
            CLASSROOM_CONFIG.TERMINAL_STATUSES.some(
                (terminalStatus) => terminalStatus === status,
            ),
    );

export const isClassroomCreating = (status?: string): boolean =>
    status === ClassroomStatus.READY_FOR_CLASSROOM_CREATION;
